import { CommonModule } from '@angular/common';
import { Component, HostBinding, Inject, OnDestroy } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Subject } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { NotificationType } from '../../../../models/notification-type';
import { NotificationService } from '../../../../services/notification/notification.service';
import { SharedModule } from '../../../../shared.module';
import { IMfaCreationResponse } from '../../models/mfa-creation-response.interface';
import { IMfaValidationResponse } from '../../models/mfa-validation-response.interface';
import { ViewAccessCodeService } from '../../view-access-code.service';
import { IMfaInputDialogForm } from './models/mfa-input-dialog-form.interface';
import { MfaInputDialogFormService } from './services/mfa-input-dialog-form.service';

@Component({
    selector: 'upsc-mfa-input-dialog',
    templateUrl: './mfa-input-dialog.component.html',
    styleUrls: ['./mfa-input-dialog.component.scss'],
    standalone: true,
    providers: [MfaInputDialogFormService, ViewAccessCodeService],
    imports: [
        CommonModule,
        MatDialogClose,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        ReactiveFormsModule,
        SharedModule,
    ],
})
export class MfaInputDialogComponent implements OnDestroy {
    @HostBinding('class') public hostClass = 'mfa-input-dialog';

    public shipmentId: string;
    public emailAddress: string;
    public mfaExpiration: Date;

    public isValidatingCode = false;
    public isResendingMFACode = false;
    public formGroup: FormGroup<IMfaInputDialogForm>;

    private ngDestroyed$ = new Subject<void>();

    public constructor(public dialogRef: MatDialogRef<MfaInputDialogComponent>,
                       @Inject(MAT_DIALOG_DATA) public data: any,
                       public readonly formService: MfaInputDialogFormService,
                       public readonly viewAccessCodeService: ViewAccessCodeService,
                       private readonly notificationService: NotificationService,
    ) {
        this.formGroup = this.formService.formGroup;

        this.shipmentId = data.shipmentId;
        this.emailAddress = data.emailAddress;
        this.mfaExpiration = data.mfaExpiration;
    }

    public ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    public submitForm(event: SubmitEvent): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        this.formGroup.controls.mfa.setErrors(null);

        this.isValidatingCode = true;
        this.viewAccessCodeService.validateMFA(this.shipmentId, this.formGroup.controls.mfa.value)
            .pipe(
                takeUntil(this.ngDestroyed$),
                catchError((err) => {
                    // this.notificationService.notify(
                    //     'There was an error while validating MFA code',
                    //     'Error while validating MFA code',
                    //     NotificationType.ERROR,
                    // );

                    this.formGroup.controls.mfa.setErrors({ validate: { valid: false } });

                    console.error(err);
                    return null;
                }),
                finalize(() => {
                    this.isValidatingCode = false;
                }),
            )
            .subscribe(
                (res: IMfaValidationResponse) => {
                    if (!res) { return; }

                    this.dialogRef.close(res);
                },
            );
    }

    public resendMFACode(event: MouseEvent): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        this.formGroup.controls.mfa.setErrors(null);
        this.formGroup.controls.mfa.setValue(null);

        this.isResendingMFACode = true;
        this.viewAccessCodeService.requestMFA(this.shipmentId)
            .pipe(
                takeUntil(this.ngDestroyed$),
                catchError((err) => {
                    this.notificationService.notify(
                        'There was an error while resending MFA',
                        'Error while resending MFA',
                        NotificationType.ERROR,
                    );

                    console.error(err);
                    return null;
                }),
                finalize(() => {
                    this.isResendingMFACode = false;
                }),
            )
            .subscribe(
                (res: IMfaCreationResponse) => {
                    if (!res) { return; }

                    this.emailAddress = res.emailAddress;
                    this.mfaExpiration = res.mfaExpiration;

                    this.notificationService.notify(
                        `A MFA code has been resent to ${ this.emailAddress }.`,
                        'Access Code Sent Successfully',
                        NotificationType.SUCCESS,
                    );
                },
            );
    }
}
